
import React from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Input,
  FormControl,
  FormLabel,
  Grid,
  Text,
  Checkbox,
} from "@chakra-ui/react";
import { AddCustomer } from "api/customer";


const PURPLE = "#7062B9";
const PURPLE_DARK = "#5B4FA3";
const PURPLE_DARKER = "#4E4390";
const NAVY = "#2D2A5E";

interface AddCustomerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit: boolean;
  form: AddCustomer;

  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;

  handleSubmit: () => void;
  handleReset: () => void;
}

const AddCustomerDrawer = ({
  isOpen,
  onClose,
  isEdit,
  form,
  handleChange,
  handleSubmit,
  handleReset,
}: AddCustomerDrawerProps) => {
  const inputStyle = {
    h: "56px",
    borderRadius: "14px",
    border: "1px solid",
    borderColor: "#D5DCE8",
    fontSize: "16px",
    px: 4,
    color: NAVY,
    bg: "white",

    _hover: {
      borderColor: "#B8C2D1",
    },

    _focus: {
      borderColor: PURPLE,
      boxShadow: `0 0 0 1px ${PURPLE}`,
    },
  };

  const labelStyle = {
    fontSize: "sm",
    fontWeight: "bold",
    color: NAVY,
    mb: "8px",
  };

  const formatDate = (date?: string) => {
    if (!date) return "";

    return date.substring(0, 10);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay bg="rgba(0, 0, 0, 0.35)" />

      <DrawerContent bg="white" borderRadius="20px 0 0 20px" overflow="hidden">
        {/* =====================================================
            HEADER
        ====================================================== */}

        <DrawerHeader
          bg={`linear-gradient(135deg, ${PURPLE_DARK} 0%, ${PURPLE} 100%)`}
          color="white"
          px={6}
          py={7}
          minH="125px"
          position="relative"
        >
          <Text fontSize="30px" fontWeight="700" lineHeight="1.2" mb={2}>
            {isEdit ? "Update Customer" : "Add Customer"}
          </Text>

          <Text fontSize="16px" fontWeight="400" opacity={0.95}>
            Enter customer information
          </Text>

          <DrawerCloseButton
            position="absolute"
            right="18px"
            top="22px"
            size="lg"
            color="white"
            fontSize="18px"
            _hover={{
              bg: "rgba(255,255,255,0.15)",
            }}
          />
        </DrawerHeader>

        {/* =====================================================
            BODY
        ====================================================== */}

        <DrawerBody
          px={6}
          py={7}
          overflowY="auto"
          css={{
            "&::-webkit-scrollbar": {
              width: "7px",
            },

            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },

            "&::-webkit-scrollbar-thumb": {
              background: "#b8b8b8",
              borderRadius: "10px",
            },
          }}
        >
          {/* ===================================================
              ONE COLUMN GRID
          ==================================================== */}

          <Grid templateColumns="1fr" gap={6}>
            {/* CUSTOMER NAME */}
            <FormControl>
              <FormLabel {...labelStyle}>Customer Name</FormLabel>

              <Input
                id="customerName"
                name="customerName"
                value={form.customerName || ""}
                onChange={handleChange}
                placeholder="Enter customer name"
                {...inputStyle}
              />
            </FormControl>

            {/* EMAIL */}
            <FormControl>
              <FormLabel {...labelStyle}>Email</FormLabel>

              <Input
                id="email"
                name="email"
                type="email"
                value={form.email || ""}
                onChange={handleChange}
                placeholder="Enter email address"
                {...inputStyle}
              />
            </FormControl>

            {/* PLANT */}
            <FormControl>
              <FormLabel {...labelStyle}>Plant Name</FormLabel>

              <Input
                id="plant"
                name="plant"
                value={form.plant || ""}
                onChange={handleChange}
                placeholder="Enter plant name"
                {...inputStyle}
              />
            </FormControl>

            {/* ADDRESS */}
            <FormControl>
              <FormLabel {...labelStyle}>Address</FormLabel>

              <Input
                id="address"
                name="address"
                value={form.address || ""}
                onChange={handleChange}
                placeholder="Enter address"
                {...inputStyle}
              />
            </FormControl>

            {/* GSTIN */}
            <FormControl>
              <FormLabel {...labelStyle}>GSTIN Number</FormLabel>

              <Input
                id="gstIn"
                name="gstIn"
                value={form.gstIn || ""}
                onChange={handleChange}
                placeholder="Enter GSTIN number"
                textTransform="uppercase"
                {...inputStyle}
              />
            </FormControl>

            {/* PHONE */}
            <FormControl>
              <FormLabel {...labelStyle}>Phone No</FormLabel>

              <Input
                id="phoneNo"
                name="phoneNo"
                type="tel"
                value={form.phoneNo || ""}
                onChange={handleChange}
                placeholder="Enter phone number"
                {...inputStyle}
              />
            </FormControl>

            {/* EQUIPMENT NAME */}
            <FormControl>
              <FormLabel {...labelStyle}>Equipment Name</FormLabel>

              <Input
                id="equipmentName"
                name="equipmentName"
                value={form.equipmentName || ""}
                onChange={handleChange}
                placeholder="Enter equipment name"
                {...inputStyle}
              />
            </FormControl>

            {/* AMC CHECKBOX */}
            <FormControl>
              <Checkbox
                id="isAMC"
                name="isAMC"
                isChecked={form.isAMC === true}
                onChange={handleChange}
                colorScheme="purple"
                size="lg"
              >
                <Text fontSize="sm" fontWeight="bold" color={NAVY} ml={2}>
                  IS AMC ACTIVE?
                </Text>
              </Checkbox>
            </FormControl>

            {/* AMC FIELDS */}
            {form.isAMC === true && (
              <>
                {/* AMC AMOUNT */}
                <FormControl>
                  <FormLabel {...labelStyle}>AMC Amount</FormLabel>

                  <Input
                    id="amcAmount"
                    name="amcAmount"
                    type="number"
                    min={0}
                    step="0.01"
                    value={
                      form.amcAmount === undefined || form.amcAmount === null
                        ? ""
                        : form.amcAmount
                    }
                    onChange={handleChange}
                    placeholder="Enter AMC amount"
                    {...inputStyle}
                  />
                </FormControl>

                {/* AMC START DATE */}
                <FormControl>
                  <FormLabel {...labelStyle}>AMC Start Date</FormLabel>

                  <Input
                    id="amcStartDate"
                    name="amcStartDate"
                    type="date"
                    value={formatDate(form.amcStartDate)}
                    onChange={handleChange}
                    {...inputStyle}
                  />
                </FormControl>

                {/* AMC END DATE */}
                <FormControl>
                  <FormLabel {...labelStyle}>AMC End Date</FormLabel>

                  <Input
                    id="amcEndDate"
                    name="amcEndDate"
                    type="date"
                    value={formatDate(form.amcEndDate)}
                    onChange={handleChange}
                    min={form.amcStartDate ? formatDate(form.amcStartDate) : undefined}
                    {...inputStyle}
                  />
                </FormControl>
              </>
            )}
          </Grid>
        </DrawerBody>

        {/* =====================================================
            FOOTER
        ====================================================== */}

        <DrawerFooter
          bg="white"
          borderTop="1px solid"
          borderColor="#E5E7EB"
          px={6}
          py={5}
          gap={4}
          justifyContent="flex-end"
        >
          {/* RESET */}
          <Button
            variant="outline"
            size="lg"
            h="50px"
            px={6}
            borderRadius="12px"
            fontSize="16px"
            fontWeight="600"
            borderColor="#D5DCE8"
            color={NAVY}
            onClick={handleReset}
            _hover={{
              bg: "#F7F8FA",
            }}
          >
            Reset
          </Button>

          {/* SAVE / UPDATE */}
          <Button
            size="lg"
            h="50px"
            px={7}
            borderRadius="12px"
            fontSize="16px"
            fontWeight="700"
            color="white"
            bg={PURPLE}
            onClick={handleSubmit}
            _hover={{
              bg: PURPLE_DARK,
            }}
            _active={{
              bg: PURPLE_DARKER,
            }}
          >
            {isEdit ? "Update Customer" : "Save Customer"}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default AddCustomerDrawer;